import React from "react";
import { Card, Icon, Popover, Empty, Tag, Spin, Progress } from "antd";

import { TrustMetrics } from "../types";

const { Meta } = Card;

type Props = {
    summary: string | undefined;
    improvements: string[] | undefined;
    trustMetrics: TrustMetrics | undefined;
    trustScore: number | undefined;
    inProgress: boolean;
}

export function getColorByScore(seoScore: number) {
    if (seoScore < 50) return '#f63842';
    if (seoScore >= 50 && seoScore < 80) return '#ffab00';
    if (seoScore >= 80) return '#34bc6e';
}

function ContentTrustScoreDescription() {
    return (
        <div className="trust-metric-description">
            <h4>What is it?</h4>
            <p>The <strong>Content Trust Score</strong> measures how reliable and trustworthy a website's content appears.</p>
            <h4>How it was evaluated?</h4>
            <p>The Content Trust Score is calculated as a weighted average of three factors: <strong>Topical Relevance (50%)</strong>, <strong>Subject Expertise (25%)</strong> and <strong>Credibility (25%)</strong>.</p>
            <p>This balance prioritizes how closely content aligns with the site's main theme, while also factoring in expertise (the depth of knowledge, detail, and accuracy shown in the content) and reliability (transparency, reputation and clarity about ownership).</p>
        </div>
    )
}

function TopicalRelevanceDescription({ text }: { text: string }) {
    return (
        <div className="trust-metric-description">
            <h4>What is it?</h4>
            <p><strong>Topical Relevance</strong> measures how well the content stays on topic and aligns with the domain's primary focus.</p>
            <h4>How it was evaluated?</h4>
            <p>The LLM checked whether most pages consistently cover the same core topics, with minimal off-topic filler or unrelated content.</p>
            <h4>LLM Evaluation Notes</h4>
            <p>{text}</p>
        </div>
    );
}

function SubjectExpertiseDescription({ text }: { text: string }) {
    return (
        <div className="trust-metric-description">
            <h4>What is it?</h4>
            <p><strong>Subject Expertise</strong> measures the depth of knowledge, accuracy, and authority demonstrated in the content.</p>
            <h4>How it was evaluated?</h4>
            <p>The LLM assessed the use of specialized terminology, comprehensive explanations, references to credible sources, and evidence of expert authorship.</p>
            <h4>LLM Evaluation Notes</h4>
            <p>{text}</p>
        </div>
    );
}

function CredibilityDescription({ text }: { text: string }) {
    return (
        <div className="trust-metric-description">
            <h4>What is it?</h4>
            <p><strong>Credibility</strong> measures the trustworthiness and the reliability signals in the website's content and presentation.</p>
            <h4>How it was evaluated?</h4>
            <p>The LLM looked for transparency (about/contact pages, author info), consistency across content, and the absence of misleading or low-quality claims.</p>
            <h4>LLM Evaluation Notes</h4>
            <p>{text}</p>
        </div>
    );
}


export default function ContentTrust({ inProgress, summary, improvements, trustMetrics, trustScore }: Props) {
    const topicalRelevance = trustMetrics?.topical_relevance;
    const subjectExpertise = trustMetrics?.subject_expertise;
    const credibility = trustMetrics?.credibility;

    return (
        <Card
            className="content-trust-analysis"
            type="inner"
            title={
                <Meta
                    title={"AI Content Trust"}
                    description={!inProgress ? "How credible your content is perceived by LLMs" : null}
                />
            }
            extra={inProgress ? <Tag color="orange">Analysis in progress <Spin indicator={<Icon type="loading-3-quarters" spin style={{ fontSize: "12px", marginLeft: "6px" }} />} /></Tag> : null}
        >
            {inProgress && (<Empty description="" />)}
            {!inProgress && !summary && !trustMetrics && !trustScore && (<Empty description="No content trust score available" />)}
            {(!inProgress && summary && trustScore && topicalRelevance && subjectExpertise && credibility && improvements) && (
                <>
                    <div className="trust-score-section">
                        <div className="title">
                            <span>Content Trust Score</span>
                            <Popover placement="leftTop" content={<ContentTrustScoreDescription />}>
                                <Icon className="tooltip" type="question-circle" theme="filled" />
                            </Popover>
                        </div>

                        <div className="score-wrapper">
                            <Progress
                                type="circle"
                                percent={trustScore}
                                width={128}
                                strokeWidth={8}
                                strokeColor={getColorByScore(trustScore)}
                                format={(percent) => `${percent}%`}
                            />

                            <div className="trust-score-components">
                                <div className="trust-score-component">
                                    <div>
                                        <span>Topical Relevance</span>
                                        <Popover placement="leftTop" content={<TopicalRelevanceDescription text={topicalRelevance.description} />}>
                                            <Icon className="tooltip" type="question-circle" theme="filled" />
                                        </Popover>
                                    </div>
                                    <Progress percent={topicalRelevance.value} strokeColor={getColorByScore(topicalRelevance.value)} />
                                </div>
                                <div className="trust-score-component">
                                    <div>
                                        <span>Subject Expertise</span>
                                        <Popover placement="leftTop" content={<SubjectExpertiseDescription text={subjectExpertise.description} />}>
                                            <Icon className="tooltip" type="question-circle" theme="filled" />
                                        </Popover>
                                    </div>
                                    <Progress percent={subjectExpertise.value} strokeColor={getColorByScore(subjectExpertise.value)} />
                                </div>
                                <div className="trust-score-component">
                                    <div>
                                        <span>Credibility</span>
                                        <Popover placement="leftTop" content={<CredibilityDescription text={credibility.description} />}>
                                            <Icon className="tooltip" type="question-circle" theme="filled" />
                                        </Popover>
                                    </div>
                                    <Progress percent={credibility.value} strokeColor={getColorByScore(credibility.value)} />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="trust-summary" dangerouslySetInnerHTML={{ __html: summary }} />
                    <div className="trust-improvements">
                        <h4>Content Trust Improvements</h4>
                        <ul className="improvements-list">
                            {improvements.map((improvement, index) => (
                                <li key={index}>
                                    <Icon type="right-circle" />
                                    <span dangerouslySetInnerHTML={{ __html: improvement }} />
                                </li>
                            ))}
                        </ul>
                    </div>
                </>
            )}
        </Card>
    );
}